import { useEffect, useRef, useState } from "react";
import type {
  ComparisonDetail,
  ComparisonFilters,
  ComparisonRelation,
} from "../../../shared/contracts/product-pages.js";
import { comparisonQuery } from "./comparison-selection.js";

interface Resource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}
const pending = { data: null, loading: true, error: null };

async function comparisonJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    headers: { accept: "application/json" },
    signal,
  });
  if (!response.ok)
    throw new Error(
      response.status === 401
        ? "Sign in with your verified account to see shared history."
        : response.status === 404
          ? "This shared record is unavailable to your account."
          : "Your shared history could not load. Try again.",
    );
  return response.json() as Promise<T>;
}

function useComparisonResource<T>(
  url: string | null,
  revision: string,
): Resource<T> {
  const key = `${url ?? ""}|${revision}`;
  const [resource, setResource] = useState<Resource<T> & { key: string }>({
    ...pending,
    key,
  });
  useEffect(() => {
    const controller = new AbortController();
    if (!url) {
      setResource({ key, data: null, loading: false, error: null });
      return;
    }
    setResource({ ...pending, key });
    void comparisonJson<T>(url, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted)
          setResource({ key, data, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setResource({
            key,
            data: null,
            loading: false,
            error:
              error instanceof Error
                ? error.message
                : "Shared history is unavailable.",
          });
      });
    return () => controller.abort();
  }, [url, key]);
  return resource.key === key ? resource : pending;
}

export function useComparisonRelations(
  revision = "",
  filters: ComparisonFilters = {},
) {
  const resource = useComparisonResource<{
    definitionVersion: 1;
    relations: ComparisonRelation[];
    availableYears: number[];
    hasSimulator: boolean;
  }>(`/api/v1/rivalries?${comparisonQuery(filters)}`, revision);
  const catalog = useRef(resource.data);
  if (resource.data) catalog.current = resource.data;
  return { ...resource, catalog: catalog.current };
}

export function useComparisonDetail(
  id: string | null,
  filters: ComparisonFilters,
  revision = "",
) {
  const base = id ? `/api/v1/rivalries/${encodeURIComponent(id)}` : null;
  const catalog = useComparisonResource<ComparisonDetail>(
    base ? `${base}?basis=gross` : null,
    revision,
  );
  const query = comparisonQuery(filters);
  const filtered = useComparisonResource<ComparisonDetail>(
    base && query !== "basis=gross" ? `${base}?${query}` : null,
    revision,
  );
  return {
    ...(query === "basis=gross" ? catalog : filtered),
    catalog: catalog.data,
  };
}
